import { Stack } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { JobStatusStyledIcon } from '@/console/features/jobs/components/job-status-styled-icon/job-status-styled-icon';
import { TTargetMenuItemProps } from '@/console/features/pipelines/components/target-menu-item/target-menu-item.types';
import { usePipelineStepType } from '@/console/features/pipelines/hooks/use-pipeline-step-type/use-pipeline-step-type';
import { DevicesIcon } from '@/shared/assets/icons/devices-icon';
import { LicenseIcon } from '@/shared/assets/icons/license-icon';
import { ProjectsIcon } from '@/shared/assets/icons/projects-icon';
import { MenuItem } from '@/shared/components/ui/menu-item/menu-item';
import { useVendor } from '@/shared/hooks/use-vendor/use-vendor';
import { TGetFeatureProps } from '@/shared/hooks/use-vendor/use-vendor.types';
import { useReadDevicesByIdsQuery } from '@/shared/redux/api/assets/devices/devices-api';
import { useReadProjectsByIdsQuery } from '@/shared/redux/api/assets/projects/projects-api';
import { isPermissionDeniedError } from '@/shared/redux/api/utils/error-handlers';
import { FeatureTypeEnum } from '@/shared/types/types.generated';
import { getProtectedResourceLabel } from '@/shared/utils/protected-resource-label';

export const TargetMenuItem: React.FC<TTargetMenuItemProps> = ({
  jobType,
  target,
  ...rest
}) => {
  const { t } = useTranslation();
  const { getStepTypeLabel } = usePipelineStepType();
  const { projectIds, deviceIds } = target;
  const projectId = projectIds?.[0];
  const deviceId = deviceIds?.[0];
  const {
    data: projectData,
    error: projectError,
    isLoading: isLoadingProject,
    isFetching: isFetchingProject,
  } = useReadProjectsByIdsQuery(
    { projectIds: projectId ? [projectId] : [] },
    { skip: !projectId },
  );
  const {
    data: deviceData,
    error: deviceError,
    isLoading: isLoadingDevice,
    isFetching: isFetchingDevice,
  } = useReadDevicesByIdsQuery(
    { deviceIds: deviceId ? [deviceId] : [] },
    { skip: !deviceId },
  );

  const project = projectData?.readProjectsByIds.items.find(
    (project) => project.projectId === projectId,
  );
  const device = deviceData?.readDevicesByIds.items.find(
    (device) => device.deviceId === deviceId,
  );
  const isProjectPending = isLoadingProject || isFetchingProject;
  const isDevicePending = isLoadingDevice || isFetchingDevice;
  const isProjectProtected =
    Boolean(projectId) &&
    !project &&
    !isProjectPending &&
    (Boolean(projectData) || isPermissionDeniedError(projectError));
  const isDeviceProtected =
    Boolean(deviceId) &&
    !device &&
    !isDevicePending &&
    (Boolean(deviceData) || isPermissionDeniedError(deviceError));

  const { getFeature } = useVendor();
  const feature = getFeature({
    feature: jobType,
    ...(device || project),
  } as TGetFeatureProps);
  const featureType = feature?.metaData.featureType;

  const isLoading = isProjectPending || isDevicePending;

  const renderIcon = () => {
    if (featureType === FeatureTypeEnum.DeviceFeature || isDeviceProtected) {
      return <DevicesIcon />;
    }

    if (featureType === FeatureTypeEnum.LicenseFeature) {
      return <LicenseIcon />;
    }

    return <ProjectsIcon />;
  };

  const renderLabel = () => {
    if (featureType === FeatureTypeEnum.DeviceFeature || isDeviceProtected) {
      return isLoading
        ? t('ui:loading')
        : device?.name || getProtectedResourceLabel(t, 'device');
    }

    if (featureType === FeatureTypeEnum.LicenseFeature) {
      return getStepTypeLabel(jobType);
    }

    return isLoading
      ? t('ui:loading')
      : project?.name || getProtectedResourceLabel(t, 'project');
  };

  const renderStatusIcon = () => {
    if (target.__typename === 'TargetExecutionStatusResponseType') {
      return <JobStatusStyledIcon status={target.status} />;
    }
    return null;
  };

  if (
    !isLoading &&
    !project &&
    !device &&
    !isProjectProtected &&
    !isDeviceProtected
  ) {
    return null;
  }

  return (
    <MenuItem icon={renderStatusIcon()} {...rest}>
      <Stack gap="0.375rem" direction="row" alignItems="center">
        {renderIcon()}
        {renderLabel()}
      </Stack>
    </MenuItem>
  );
};
